// Two-step email binding dialog: enter email → send code → enter 6-digit code → bind.
//
// Reused by:
//   - TopBar account menu "Email" entry (first bind or change)
//   - EmailReminder (the proactive bind prompt)
//
// On success it toasts and calls onDone(result); the parent is responsible for
// refreshing /me so the new email propagates into the AuthProvider.

import React from 'react';
import { Dialog, DialogHeader, DialogBody } from '../ui/index.js';
import { XPrim } from '../primitives.jsx';
import { useT } from '../i18n.jsx';
import { sendEmailCode, verifyEmailCode } from '../api/email.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EmailVerifyModal = ({ initialEmail = '', isChange = false, onClose, onDone }) => {
  const { t } = useT();
  const { toast } = XPrim.useToast();
  const [step, setStep] = React.useState('email'); // 'email' | 'code'
  const [email, setEmail] = React.useState(initialEmail);
  const [code, setCode] = React.useState('');
  const [sentTo, setSentTo] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [cooldown, setCooldown] = React.useState(0); // 重新发送倒计时（秒）

  // 60s 重发冷却：每秒递减，归零后才允许再次发送。
  React.useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const doSend = async (e) => {
    if (e) e.preventDefault();
    setErr(null);
    const addr = email.trim();
    if (!EMAIL_RE.test(addr)) { setErr(t('email.modal.invalidEmail')); return; }
    setBusy(true);
    try {
      const r = await sendEmailCode(addr);
      setSentTo((r && r.email) || addr);
      setCode('');
      setStep('code');
      setCooldown(60);
    } catch (e2) {
      setErr((e2 && e2.message) || t('email.modal.sendFailed'));
    } finally {
      setBusy(false);
    }
  };

  const doVerify = async (e) => {
    if (e) e.preventDefault();
    setErr(null);
    if (!/^\d{6}$/.test(code.trim())) { setErr(t('email.modal.invalidCode')); return; }
    setBusy(true);
    try {
      const r = await verifyEmailCode(code.trim());
      toast(t('email.modal.success'));
      if (onDone) onDone(r);
    } catch (e2) {
      setErr((e2 && e2.message) || t('email.modal.verifyFailed'));
    } finally {
      setBusy(false);
    }
  };

  const title = isChange ? t('email.modal.changeTitle') : t('email.modal.title');

  return (
    <Dialog open={true} onClose={busy ? null : onClose} maxWidth={440}>
      <DialogHeader title={title} onClose={busy ? null : onClose} />
      <DialogBody>
        {step === 'email' ? (
          <form onSubmit={doSend}>
            <p style={{ fontSize: 12.5, color: 'var(--fg-3)', lineHeight: 1.7, margin: '0 0 14px' }}>
              {t('email.modal.emailHint')}
            </p>
            <label className="field-label">{t('email.modal.emailLabel')}</label>
            <input
              className="input"
              autoFocus
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setErr(null); }}
            />
            {err && <div style={{ color: 'var(--err)', fontSize: 12, marginTop: 8 }}>{err}</div>}
            <div className="row gap-2" style={{ marginTop: 18, justifyContent: 'flex-end' }}>
              <button type="button" className="btn" onClick={onClose} disabled={busy}>
                {t('email.modal.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={busy || cooldown > 0}>
                {busy
                  ? <><div className="spinner" /> {t('email.modal.sending')}</>
                  : cooldown > 0 ? t('email.modal.resendIn').replace('{s}', cooldown) : t('email.modal.sendCode')}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={doVerify}>
            <p style={{ fontSize: 12.5, color: 'var(--fg-3)', lineHeight: 1.7, margin: '0 0 14px' }}>
              {t('email.modal.codeSentTo').replace('{email}', sentTo)}
            </p>
            <label className="field-label">{t('email.modal.codeLabel')}</label>
            <input
              className="input mono"
              autoFocus
              inputMode="numeric"
              maxLength={6}
              placeholder="••••••"
              value={code}
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setErr(null); }}
              style={{
                textAlign: 'center',
                // letterSpacing 在每个字符右侧加间距；textIndent 等量补偿尾部间距以视觉居中
                letterSpacing: '0.55em',
                textIndent: '0.55em',
                fontSize: 26,
                fontWeight: 600,
                padding: '14px 0',
                fontVariantNumeric: 'tabular-nums',
              }}
            />
            {err && <div style={{ color: 'var(--err)', fontSize: 12, marginTop: 8 }}>{err}</div>}
            <div className="row between" style={{ marginTop: 12 }}>
              <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => { setStep('email'); setErr(null); }}>
                {t('email.modal.changeEmail')}
              </button>
              <button type="button" className="btn btn-sm btn-ghost" disabled={busy || cooldown > 0} onClick={doSend}>
                {cooldown > 0 ? t('email.modal.resendIn').replace('{s}', cooldown) : t('email.modal.resend')}
              </button>
            </div>
            <div className="row gap-2" style={{ marginTop: 18, justifyContent: 'flex-end' }}>
              <button type="button" className="btn" onClick={onClose} disabled={busy}>
                {t('email.modal.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? <><div className="spinner" /> {t('email.modal.verifying')}</> : t('email.modal.verify')}
              </button>
            </div>
          </form>
        )}
      </DialogBody>
    </Dialog>
  );
};

export default EmailVerifyModal;
